import { useMemo } from 'react';
import type { ExecutionDataReportRow } from '@monitoring-panel/types';
import { ReportTable, type ReportColumn } from './ReportTable';
import { formatInt } from '@/lib/report-format';

interface ReportExecutionsDataProps {
  rows: ExecutionDataReportRow[];
}

/**
 * `executions_data` has a dynamic schema (merged BigQuery tables). Columns are
 * inferred from the union of keys across the first rows; numeric values are
 * formatted with thousands separators.
 */
export function ReportExecutionsData({ rows }: ReportExecutionsDataProps) {
  const columns = useMemo<ReportColumn<ExecutionDataReportRow>[]>(() => {
    const keys = new Set<string>();
    for (const row of rows.slice(0, 50)) {
      for (const key of Object.keys(row)) keys.add(key);
    }

    return Array.from(keys).map((key) => ({
      key,
      label: prettifyKey(key),
      align: 'left',
      render: (row: ExecutionDataReportRow) => {
        const value = row[key];
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') return formatInt(value);
        return String(value);
      },
    }));
  }, [rows]);

  return <ReportTable rows={rows} columns={columns} initialLimit={20} emptyLabel="No executions data" />;
}

function prettifyKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
